import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Capacitor } from "@capacitor/core";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Scan, LogOut, Shield, Search, SearchX, PackageSearch, MessageSquare, Info, Heart, Globe, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import FavoriteButton from "@/components/FavoriteButton";
import { establishBackendSession } from "@/auth/session";
import { statusBadgeStyle } from "@/lib/product-status";
import { flagFor, prettyCountry } from "@/lib/countries";
import AppHeader from "@/components/AppHeader";
import EmptyState from "@/components/EmptyState";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const productPageUrl = (query, skip, countries, viewMode) =>
  `${API}/products?limit=${PAGE_SIZE}&skip=${skip}&q=${encodeURIComponent(query)}` +
  `&countries=${encodeURIComponent((countries || []).join(','))}` +
  `&mine=${viewMode === 'mine'}&reviewed=${viewMode === 'reviewed'}`;

const VIEW_TITLES = {
  all: ['Products Database', 'registered products'],
  favorites: ['My Favorites', 'favorites'],
  mine: ['My Products', 'added by you'],
  reviewed: ['Reviewed by Me', 'products you reviewed'],
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, logout: auth0Logout } = useAuth0();
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  // Search and view survive a round-trip to a product page (per-tab)
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem('dash_q') || '');
  const [debouncedQuery, setDebouncedQuery] = useState(() => sessionStorage.getItem('dash_q') || '');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [productCount, setProductCount] = useState(0);
  // Country filter persists across visits (last choice is remembered)
  const [countryFilter, setCountryFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('country_filter') || '[]'); } catch { return []; }
  });
  const [countryOptions, setCountryOptions] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [viewMode, setViewMode] = useState(() => sessionStorage.getItem('dash_view') || 'all'); // all | favorites | mine | reviewed

  // The backend session is guaranteed by <ProtectedRoute>. If it went stale
  // mid-visit (a 401 clears the token), re-mint it once from the still-valid
  // Auth0 token and retry instead of bouncing back to the landing page.
  const withSessionRetry = useCallback(async (fn) => {
    try {
      return await fn();
    } catch (error) {
      if (error?.response?.status !== 401) throw error;
      await establishBackendSession(getAccessTokenSilently);
      return await fn();
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    sessionStorage.setItem('dash_q', searchQuery);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    sessionStorage.setItem('dash_view', viewMode);
  }, [viewMode]);

  // One-time extras: country options for the filter + the user's favorite ids
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [c, f] = await withSessionRetry(() => Promise.all([
          axios.get(`${API}/products/countries`),
          axios.get(`${API}/me/favorites/ids`),
        ]));
        if (!active) return;
        setCountryOptions(c.data);
        setFavoriteIds(new Set(f.data.product_ids));
      } catch (error) {
        console.error('Error loading filters:', error);
      }
    })();
    return () => { active = false; };
  }, [withSessionRetry]);

  // Search/filters run server-side so they cover the whole collection —
  // the previous client-side filter silently searched only the first page.
  useEffect(() => {
    let active = true;

    const run = async () => {
      try {
        const data = await withSessionRetry(async () => {
          const [me, list, count] = await Promise.all([
            axios.get(`${API}/auth/me`),
            viewMode === 'favorites'
              ? axios.get(`${API}/me/favorites?limit=${PAGE_SIZE}&skip=0`)
              : axios.get(productPageUrl(debouncedQuery, 0, countryFilter, viewMode)),
            viewMode === 'favorites'
              ? axios.get(`${API}/me/favorites/count`)
              : axios.get(`${API}/products/count?q=${encodeURIComponent(debouncedQuery)}&countries=${encodeURIComponent(countryFilter.join(','))}&mine=${viewMode === 'mine'}&reviewed=${viewMode === 'reviewed'}`),
          ]);
          return { user: me.data, products: list.data, count: count.data.count };
        });
        if (!active) return;
        setUser(data.user);
        setProducts(data.products);
        setProductCount(data.count);
        setLoadError(false);
      } catch (error) {
        console.error('Error:', error);
        if (active) setLoadError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    return () => {
      active = false;
    };
  }, [withSessionRetry, debouncedQuery, reloadKey, countryFilter, viewMode]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const url = viewMode === 'favorites'
        ? `${API}/me/favorites?limit=${PAGE_SIZE}&skip=${products.length}`
        : productPageUrl(debouncedQuery, products.length, countryFilter, viewMode);
      const { data } = await withSessionRetry(() => axios.get(url));
      setProducts((prev) => [...prev, ...data]);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Could not load more products. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleCountry = (slug) => {
    setCountryFilter((prev) => {
      const next = prev.includes(slug) ? prev.filter((c) => c !== slug) : [...prev, slug];
      localStorage.setItem('country_filter', JSON.stringify(next));
      return next;
    });
  };

  const clearCountries = () => {
    localStorage.setItem('country_filter', '[]');
    setCountryFilter([]);
  };

  // FavoriteButton owns the guard/API; we only sync local state here.
  const onFavoriteChanged = (product, { favorited, favorites_count }) => {
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (favorited) next.add(product.id); else next.delete(product.id);
      return next;
    });
    setProducts((prev) => viewMode === 'favorites' && !favorited
      ? prev.filter((p) => p.id !== product.id)
      : prev.map((p) => (p.id === product.id ? { ...p, favorites_count } : p)));
    if (viewMode === 'favorites' && !favorited) setProductCount((c) => Math.max(0, c - 1));
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      if (Capacitor.isNativePlatform()) {
        localStorage.clear();
        auth0Logout({ logoutParams: { returnTo: 'be.rmworx.cilantrosafe://callback' }, openUrl: false });
        navigate('/');
      } else {
        auth0Logout({ logoutParams: { returnTo: window.location.origin } });
      }
    }
  };

  const handleProductClick = (barcode) => {
    navigate(`/product/${barcode}`);
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <AppHeader />
        <div className="dashboard-content">
          <div className="skeleton" style={{ height: '2.2rem', width: '280px', marginBottom: '2rem' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: '170px' }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="dashboard-container" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', textAlign: 'center', padding: '1rem' }}>
        <p>We couldn't load your dashboard. Please try again.</p>
        <Button onClick={() => setReloadKey((k) => k + 1)} data-testid="dashboard-retry">Retry</Button>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <AppHeader
        right={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/about')}
              data-testid="about-button"
            >
              <Info className="w-4 h-4 mr-2" />
              About
            </Button>
            {user?.is_admin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                data-testid="admin-panel-button"
              >
                <Shield className="w-4 h-4 mr-2" />
                Admin Panel
              </Button>
            )}
            <Avatar data-testid="user-avatar">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </>
        }
      />

      <div className="dashboard-content">
        {/* Compact welcome row: the products are the content, not the greeting */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.6rem', fontWeight: 700 }} data-testid="dashboard-welcome">
              Welcome, {user?.name}!
            </h1>
            <p style={{ fontSize: '0.95rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
              Scan a product or browse the community database
            </p>
          </div>
          <Button
            size="lg"
            onClick={() => navigate('/scan')}
            style={{ background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)', fontSize: '1rem', padding: '1.4rem 2rem' }}
            data-testid="scan-product-button"
          >
            <Scan className="w-5 h-5 mr-2" />
            Scan Product
          </Button>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h2 style={{ fontSize: '1.8rem', fontWeight: 600 }}>{VIEW_TITLES[viewMode][0]}</h2>
              <p style={{ fontSize: '0.9rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                {productCount.toLocaleString()} {VIEW_TITLES[viewMode][1]}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', flex: '1', justifyContent: 'flex-end', minWidth: '250px' }}>
              {viewMode !== 'favorites' && (
                <>
                  <div style={{ position: 'relative', maxWidth: '320px', flex: '1', minWidth: '180px' }}>
                    <Search className="w-4 h-4" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'hsl(var(--muted-foreground))' }} />
                    <Input
                      placeholder="Search products by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ paddingLeft: '2.5rem' }}
                      data-testid="search-input"
                    />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" data-testid="country-filter">
                        <Globe className="w-4 h-4 mr-2" />
                        {countryFilter.length === 0
                          ? 'All countries'
                          : countryFilter.length <= 3
                            ? countryFilter.map(flagFor).join(' ')
                            : `${countryFilter.slice(0, 3).map(flagFor).join(' ')} +${countryFilter.length - 3}`}
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" style={{ maxHeight: '340px', overflowY: 'auto' }}>
                      <DropdownMenuItem onClick={clearCountries} data-testid="country-all">
                        All countries
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {countryOptions.map(({ country, count }) => (
                        <DropdownMenuCheckboxItem
                          key={country}
                          checked={countryFilter.includes(country)}
                          onCheckedChange={() => toggleCountry(country)}
                          onSelect={(e) => e.preventDefault()}
                          data-testid={`country-${country}`}
                        >
                          <span style={{ marginRight: '0.5rem' }}>{flagFor(country)}</span>
                          {prettyCountry(country)}
                          <span style={{ marginLeft: 'auto', paddingLeft: '1rem', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                            {count.toLocaleString()}
                          </span>
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                <Button variant={viewMode === 'all' ? 'default' : 'outline'} size="sm"
                  onClick={() => setViewMode('all')} data-testid="view-all">
                  All
                </Button>
                <Button variant={viewMode === 'favorites' ? 'default' : 'outline'} size="sm"
                  onClick={() => setViewMode('favorites')} data-testid="view-favorites">
                  <Heart className="w-4 h-4 mr-1" fill={viewMode === 'favorites' ? 'currentColor' : 'none'} />
                  Favorites
                </Button>
                <Button variant={viewMode === 'mine' ? 'default' : 'outline'} size="sm"
                  onClick={() => setViewMode('mine')} data-testid="view-mine">
                  <PackageSearch className="w-4 h-4 mr-1" />
                  Mine
                </Button>
                <Button variant={viewMode === 'reviewed' ? 'default' : 'outline'} size="sm"
                  onClick={() => setViewMode('reviewed')} data-testid="view-reviewed">
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Reviewed
                </Button>
              </div>
            </div>
          </div>

          {products.length === 0 ? (
            viewMode === 'favorites' ? (
              <EmptyState icon={Heart} title="No favorites yet" hint="Tap the heart on a product to keep it here." testId="empty-favorites" />
            ) : viewMode === 'mine' ? (
              <EmptyState icon={PackageSearch} title="No products added by you yet" hint="Scan a product that isn't in the database to add it." testId="empty-mine" />
            ) : viewMode === 'reviewed' ? (
              <EmptyState icon={MessageSquare} title="No reviews yet" hint="Open a product and share your experience." testId="empty-reviewed" />
            ) : searchQuery || countryFilter.length > 0 ? (
              <EmptyState icon={SearchX} title="No products found" hint="Try a different search term or country filter, or scan the product to add it." testId="empty-search" />
            ) : (
              <EmptyState icon={PackageSearch} title="No products yet" hint="Be the first to scan and add one!" testId="empty-products" />
            )
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
              {products.map((product) => (
                <Card
                  key={product.id}
                  className="product-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleProductClick(product.barcode)}
                  data-testid={`product-card-${product.id}`}
                >
                  <CardHeader>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '0.5rem' }}>
                      <CardTitle>{product.name}</CardTitle>
                      {(() => {
                        const badge = statusBadgeStyle(product);
                        return (
                          <Badge
                            style={{ background: badge.background, color: badge.color, whiteSpace: 'nowrap' }}
                            data-testid={`product-status-${product.id}`}
                          >
                            {badge.label}
                          </Badge>
                        );
                      })()}
                    </div>
                    <CardDescription>Barcode: {product.barcode}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {product.image_url && (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        style={{ width: '100%', aspectRatio: '16 / 10', objectFit: 'cover', borderRadius: '8px', marginBottom: '1rem' }}
                      />
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        {product.locked_by_admin && (
                          <Badge variant="outline">🔒 Verified</Badge>
                        )}
                        {product.reviews && product.reviews.length > 0 && (
                          <span style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
                            {product.reviews.length} review{product.reviews.length > 1 ? 's' : ''}
                          </span>
                        )}
                        {user && product.reviews?.some((r) => r.user_id === user.id) && (
                          <Badge variant="secondary" data-testid={`reviewed-by-me-${product.id}`}>
                            💬 You reviewed
                          </Badge>
                        )}
                      </div>
                      <FavoriteButton
                        product={product}
                        favorited={favoriteIds.has(product.id)}
                        onChanged={(change) => onFavoriteChanged(product, change)}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {products.length > 0 && products.length < productCount && (
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="load-more-button"
              >
                {loadingMore ? 'Loading…' : `Load more (${products.length} of ${productCount.toLocaleString()})`}
              </Button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Dashboard;